/**
 * @file overlay-focus.ts
 * @description Open auto-focus handling shared by every Radix overlay surface
 *              (Dialog, Sheet, AlertDialog, Popover): focus exactly the element
 *              Radix would have focused, but never leave a text field's
 *              existing value highlighted.
 * @author William Donaldson
 * @last_modified 2026-07-30
 */

/**
 * WHY THIS FILE EXISTS
 *
 * Radix's FocusScope auto-focuses the first tabbable element when an overlay
 * mounts, via `focusFirst(candidates, { select: true })`, and its internal
 * `focus()` helper calls `element.select()` on any `HTMLInputElement`
 * (@radix-ui/react-focus-scope 1.1.7, dist/index.mjs:81 and :172-178).
 *
 * For read-only or empty forms that is harmless. For our edit dialogs — "Edit
 * Obligation", "Edit Work History", "Edit Education", "Edit ID" and ~30 more —
 * the first field is pre-filled with real data (a CAQH number, an employer, a
 * license number). Opening one leaves that value fully selected, so a single
 * stray keystroke replaces the entire field, and the resulting empty-looking
 * form can then be saved without the user ever realising they wiped it.
 *
 * This keeps Radix's focus behaviour (same target element, same `preventScroll`,
 * same container fallback) and drops only the selection.
 *
 * SCOPE NOTE — the CLOSE path needs no equivalent. FocusScope's unmount handler
 * also restores focus with `select: true` (dist/index.mjs:94), but every content
 * component we wrap already prevents that default and focuses its trigger
 * instead (react-dialog dist/index.mjs:146-149 and :176-184, react-popover
 * dist/index.mjs:141-144 and :176-184), so nothing there can highlight a field.
 */

/**
 * Radix skips anchors when auto-focusing (`removeLinks`), so a dialog whose
 * first tabbable element is a link focuses the following control instead.
 */
const LINK_TAG_NAME = "A";

/** Elements that expose a `disabled` flag (input, button, select, textarea...). */
type MaybeDisabledElement = HTMLElement & { disabled?: boolean };

function isDisabled(element: HTMLElement): boolean {
  return (element as MaybeDisabledElement).disabled === true;
}

/**
 * Mirror of Radix's own `getTabbableCandidates`
 * (@radix-ui/react-focus-scope 1.1.7, dist/index.mjs:143-154).
 *
 * WHY MIRRORED RATHER THAN APPROXIMATED: we take over focusing the overlay, so
 * if this walker picked a different element than Radix's, the fix would quietly
 * change which field every dialog in the app opens on. The filter must stay
 * byte-for-byte equivalent: skip disabled/hidden nodes and hidden inputs, keep
 * anything with a non-negative tabIndex, in document order.
 */
function getTabbableCandidates(container: HTMLElement): HTMLElement[] {
  const candidates: HTMLElement[] = [];

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) => {
      const element = node as HTMLElement;
      const isHiddenInput =
        element.tagName === "INPUT" &&
        (element as HTMLInputElement).type === "hidden";

      if (isDisabled(element) || element.hidden || isHiddenInput) {
        return NodeFilter.FILTER_SKIP;
      }

      return element.tabIndex >= 0
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_SKIP;
    },
  });

  while (walker.nextNode()) {
    const element = walker.currentNode as HTMLElement;
    if (element.tagName !== LINK_TAG_NAME) candidates.push(element);
  }

  return candidates;
}

/**
 * Focus the first tabbable element inside `container` without selecting its
 * contents, falling back to the container itself — the same two-step Radix
 * performs, minus `select: true`.
 */
export function focusFirstCandidateWithoutSelecting(
  container: HTMLElement
): void {
  const previouslyFocusedElement = document.activeElement;

  for (const candidate of getTabbableCandidates(container)) {
    // WHY preventScroll: matches Radix, so opening an overlay never yanks the
    // page (or a long scrollable form) to the focused field.
    candidate.focus({ preventScroll: true });
    // WHY re-read activeElement: a candidate can refuse focus (display:none is
    // not caught by the tabIndex filter above), so keep walking until one takes.
    if (document.activeElement !== previouslyFocusedElement) return;
  }

  // WHY THIS FALLBACK IS MANDATORY: once we call preventDefault(), Radix does
  // nothing at all — no focusFirst, no container focus. Without this line an
  // overlay with no focusable field would leave focus on the trigger outside
  // the (aria-hidden) overlay, stranding keyboard and screen reader users.
  container.focus({ preventScroll: true });
}

/**
 * Build the `onOpenAutoFocus` handler for an overlay's content element.
 *
 * Runs the caller's handler first so existing opt-outs still win (e.g. the
 * email compose dialog suppresses auto-focus entirely to avoid a mobile
 * keyboard jump), then takes over focusing so nothing gets selected.
 */
export function openAutoFocusWithoutSelection(
  onOpenAutoFocus?: (event: Event) => void
): (event: Event) => void {
  return (event: Event) => {
    onOpenAutoFocus?.(event);
    // The caller opted out of auto-focus altogether — respect that and stop.
    if (event.defaultPrevented) return;

    // WHY RESOLVED BEFORE preventDefault(): once the default is prevented Radix
    // does nothing at all, so suppressing it without a container to focus into
    // would strand focus outside the overlay. Bailing first degrades to Radix's
    // stock behaviour instead, which is unreachable in practice (the event is
    // dispatched on the content element) but is the safe direction to fail.
    const container = event.currentTarget ?? event.target;
    if (!(container instanceof HTMLElement)) return;

    // Radix reads defaultPrevented only AFTER this handler returns, so
    // preventing the default is the one way to stop its select-all — and the
    // reason focusing the field below is now our job.
    event.preventDefault();

    focusFirstCandidateWithoutSelecting(container);
  };
}
